/**
 * The Recurring tab (TMMAPP-0010; moved out of the Cashflow tab and rebuilt to prototype v2 by TMMAPP-0101,
 * audit 06 § B3, v2 L1455–1520): one card, "Recurring and one-off lines", visible to everyone — a reader
 * sees it read-only. A writer gets the outlined "+ New recurring transaction" and (D1) "Spread evenly" for
 * a budget. The column header (`ListTableHeader`) is [toggle] Line · Type · Amount · [actions]; a row is
 * a 22×22 "+" / "−" toggle, the label (the line's name, else its note, else its kind) with
 * "{Monthly | Quarterly | Annually | One time} · from {date}", the type pill, the signed amount and the
 * row menu (Edit, Delete, and — D1 — Delete spread). A toggled row shows its next occurrences, up to 6
 * within 12 months, on `surface2` with a mini Dismiss / Restore. These are the envelope's own lines: no
 * allocations; money allocated in from a bank account is in the Cashflow forecast.
 */
import { useCallback, useEffect, useState } from 'react';
import { MoneyParts } from '@/components/MoneyText';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AddIcon, KebabIcon } from '@/components/icons';
import { ObjectMenu } from '@/shell/OverflowMenu';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, isApiError, likelyDuplicate } from '@/api/errors';
import { errorCopy } from '@/api/errorCopy';
import { planLinesApi, type PlanLine } from '@/api/planLines';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ListTableHeader } from '@/components/ListTableHeader';
import { Pill } from '@/components/Pill';
import { useConfirm } from '@/components/useConfirm';
import { describeRecurrence } from '@/features/planned/recurrence/recurrence';
import { applyBackendFields } from '@/features/shared/useFieldErrors';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { duplicateSentence } from './likelyDuplicate';
import { PlanLineDialog, SpreadDialog } from './PlanLineDialogs';
import { toCivil } from './window';

const COLUMNS = '22px minmax(0, 1fr) 96px 92px 26px';
/** The next occurrences a toggled row shows: at most six, within a year. */
const NEXT_SHOWN = 6;
const LOOKAHEAD_MONTHS = 12;

interface PlanLinesPanelProps {
  envelopeId: number;
  envelopeName: string;
  currency: string | null;
  budget: boolean;
  /** A writer adds, edits, deletes and dismisses; a reader sees the lines. */
  canWrite: boolean;
  onChanged: () => void | Promise<void>;
  onNotify: (message: string) => void;
  onSubscriptionRequired: () => void;
}

type DialogState = { kind: 'add' } | { kind: 'edit'; line: PlanLine } | { kind: 'spread' } | null;

/** "{Monthly | Quarterly | Annually | One time}" for the simple rules, the full summary for the rest. */
function cadenceLabel(line: PlanLine, locale: string, t: ReturnType<typeof useTranslation>['t']) {
  const rule = line.recurrence;
  const simple = rule.interval === null || rule.interval === 1;
  if (simple && ['ONCE', 'MONTHLY', 'QUARTERLY', 'YEARLY'].includes(rule.frequency)) {
    return t(`planLines.cadenceLabel.${rule.frequency}`);
  }
  return describeRecurrence(rule, locale, t);
}

function lookaheadEnd(from: string): string {
  const year = Number(from.slice(0, 4));
  const month = Number(from.slice(5, 7)) - 1 + LOOKAHEAD_MONTHS;
  return toCivil(new Date(year, month, Number(from.slice(8, 10))));
}

export function PlanLinesPanel({
  envelopeId,
  envelopeName,
  currency,
  budget,
  canWrite,
  onChanged,
  onNotify,
  onSubscriptionRequired,
}: PlanLinesPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const [rows, setRows] = useState<PlanLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; line: PlanLine } | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const deleting = useConfirm<{ spread: boolean; line: PlanLine }>();
  // A failed write shows inside the open dialog; each bump moves focus to it (A11Y-010).
  const [errorTick, setErrorTick] = useState(0);
  // A write is in flight: the open dialog shows its loading state and is not dismissible.
  const [writing, setWriting] = useState(false);
  // SUBSCRIPTION_REQUIRED from an open dialog's write shows the subscribe card in that dialog
  // (TMMAPP-0097 §C 25); with no dialog open, the page shows it.
  const [subscribeInDialog, setSubscribeInDialog] = useState(false);
  // A `400 VALIDATION_FAILED` on `name` lands on the Name field of the open dialog.
  const [nameError, setNameError] = useState<string | null>(null);
  // A `409 PLAN_LINE_LIKELY_DUPLICATE` on the open dialog's write (TMMAPP-0173 item 8): its warning.
  const [duplicate, setDuplicate] = useState<string | null>(null);
  const subscribeStep = {
    required: subscribeInDialog,
    onCancel: () => setSubscribeInDialog(false),
  };
  const today = toCivil(new Date());
  const horizon = lookaheadEnd(today);

  const load = useCallback(async () => {
    try {
      setRows(await planLinesApi.list(envelopeId));
      setError(null);
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onSubscriptionRequired();
      } else {
        setError(t('planLines.errorGeneric'));
      }
      setRows((prev) => prev ?? []);
    }
  }, [envelopeId, onSubscriptionRequired, t]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next = await planLinesApi.list(envelopeId);
        if (cancelled) return;
        setRows(next);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
          onSubscriptionRequired();
        } else {
          setError(t('planLines.errorGeneric'));
        }
        setRows((prev) => prev ?? []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [envelopeId, onSubscriptionRequired, t]);

  // A dialog opens clean: an earlier failed write is not shown inside it.
  function openDialog(next: DialogState) {
    setError(null);
    setSubscribeInDialog(false);
    setNameError(null);
    setDuplicate(null);
    setDialog(next);
  }

  async function afterWrite() {
    await load();
    await onChanged();
  }

  async function run(fn: () => Promise<void>, ok?: string) {
    setError(null);
    setNameError(null);
    setDuplicate(null);
    setWriting(true);
    try {
      await fn();
      if (ok !== undefined) onNotify(ok);
      await afterWrite();
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        if (dialog !== null) setSubscribeInDialog(true);
        else onSubscriptionRequired();
      } else if (isApiError(err) && err.code === ErrorCodes.VALIDATION_FAILED) {
        const result = applyBackendFields(
          err,
          ['name'],
          (_field, message) => setNameError(message),
          t,
        );
        // tmm-api's plan-line service answers `details.field = "name"` (singular, no `fields` map).
        const backend = err.details.backend;
        const singular =
          typeof backend === 'object' && backend !== null && backend.field === 'name';
        if (singular) setNameError(t('planLines.nameRequired'));
        else if (result.placed === 0) setError(t('planLines.errorGeneric'));
      } else if (isApiError(err) && err.code === ErrorCodes.ENVELOPE_KIND_MISMATCH) {
        setError(t('planLines.errorKind'));
      } else if (likelyDuplicate(err) !== null) {
        setDuplicate(duplicateSentence(t, likelyDuplicate(err)!, currency, locale));
      } else {
        // The shared write-error copy names `CONCURRENT_UPDATE` (the envelope is being moved right now):
        // say so and refetch the lines and the envelope.
        const copy = errorCopy(t, err, 'planLines.errorGeneric');
        if (isApiError(err) && err.code === ErrorCodes.CONCURRENT_UPDATE) await afterWrite();
        setError(copy);
      }
      setErrorTick((n) => n + 1);
    } finally {
      setWriting(false);
    }
  }

  async function handleDelete() {
    await deleting.run(
      async ({ spread, line }) => {
        if (spread && line.spreadGroup !== null) {
          await planLinesApi.removeSpread(envelopeId, line.spreadGroup);
        } else {
          await planLinesApi.remove(envelopeId, line.id);
        }
        onNotify(t('planLines.deleted'));
        await afterWrite();
      },
      (err) => {
        if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
          onSubscriptionRequired();
          return null;
        }
        if (isApiError(err) && err.code === ErrorCodes.CONCURRENT_UPDATE) void afterWrite();
        return errorCopy(t, err, 'planLines.errorGeneric');
      },
    );
  }

  const list = rows ?? [];
  const labelOf = (line: PlanLine) =>
    line.name !== null && line.name.length > 0
      ? line.name
      : line.note !== null && line.note.length > 0
        ? line.note
        : t(`planned.kind.${line.kind}`);

  function occurrences(line: PlanLine) {
    const dates = [...new Set([...line.nextOccurrences, ...line.dismissed])]
      .filter((d) => d >= today && d <= horizon)
      .sort()
      .slice(0, NEXT_SHOWN);
    return (
      <Box
        data-occurrences={line.id}
        sx={{ bgcolor: tokens.surface2, borderTop: `1px solid ${tokens.line}` }}
      >
        {dates.map((d) => {
          const dismissed = line.dismissed.includes(d);
          return (
            <Stack
              key={d}
              direction="row"
              sx={{
                alignItems: 'center',
                gap: '10px',
                p: wide ? '8px 20px 8px 50px' : '8px 16px',
                borderBottom: `1px solid ${tokens.line}`,
              }}
            >
              <Box
                component="span"
                sx={{
                  flex: '0 0 76px',
                  fontSize: 12,
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  ...(dismissed ? { textDecoration: 'line-through', color: tokens.text2 } : {}),
                }}
              >
                {formatCivilDate(d, locale, { month: 'short', day: 'numeric' })}
              </Box>
              <Box
                component="span"
                sx={{ flex: '1 1 auto', minWidth: 0, fontSize: 11, color: tokens.text2 }}
              >
                {dismissed ? t('planLines.dismissed') : ''}
              </Box>
              <Box
                component="span"
                sx={{
                  flex: '0 0 92px',
                  textAlign: 'end',
                  fontSize: 12,
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  ...(dismissed ? { textDecoration: 'line-through', color: tokens.text2 } : {}),
                }}
              >
                <MoneyParts minor={Math.abs(line.amountMinor)} currency={currency} />
              </Box>
              {canWrite && (
                <Button
                  variant="outlined"
                  aria-label={t(dismissed ? 'planLines.restoreOn' : 'planLines.dismissOn', {
                    date: formatCivilDate(d, locale),
                  })}
                  onClick={() =>
                    void run(async () => {
                      if (dismissed) await planLinesApi.restore(envelopeId, line.id, d);
                      else await planLinesApi.dismiss(envelopeId, line.id, d);
                    })
                  }
                  sx={{
                    flex: '0 0 auto',
                    minHeight: 0,
                    p: '3px 10px',
                    fontSize: 11,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t(dismissed ? 'planLines.restore' : 'planLines.dismiss')}
                </Button>
              )}
            </Stack>
          );
        })}
        {dates.length === 0 && (
          <Box sx={{ p: '10px 20px 10px 50px', fontSize: 12, color: tokens.text2 }}>
            {t('planLines.noUpcoming')}
          </Box>
        )}
      </Box>
    );
  }

  const toggle = (line: PlanLine, open: boolean) => (
    <Box
      component="button"
      type="button"
      aria-expanded={open}
      aria-label={t('planLines.showNext', { name: labelOf(line) })}
      onClick={() => setExpanded(open ? null : line.id)}
      sx={{
        flex: '0 0 22px',
        width: 22,
        height: 22,
        mt: '2px',
        bgcolor: 'transparent',
        border: `1px solid ${tokens.line}`,
        borderRadius: '3px',
        color: tokens.text2,
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        fontSize: 13,
        lineHeight: 1,
        '&:hover': { borderColor: tokens.brandBoundary, color: tokens.link },
        '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
      }}
    >
      <span aria-hidden>{open ? '−' : '+'}</span>
    </Box>
  );

  const kebab = (line: PlanLine) =>
    canWrite ? (
      <IconButton
        aria-label={t('planLines.rowActions', { name: labelOf(line) })}
        aria-haspopup="menu"
        onClick={(event) => setMenu({ anchor: event.currentTarget, line })}
        sx={{ flex: '0 0 26px', width: 26, height: 26, p: 0, color: tokens.text2 }}
      >
        <KebabIcon size={15} />
      </IconButton>
    ) : (
      <Box sx={{ flex: '0 0 26px' }} />
    );

  // A plan line is unsigned and ink (contract v2 §13, TMMAPP-0154 §A 1).

  return (
    <Paper component="section" aria-labelledby="plan-lines-title" sx={{ p: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        data-card-header=""
        sx={{
          alignItems: 'center',
          gap: '10px 12px',
          flexWrap: 'wrap',
          p: '16px 20px',
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography id="plan-lines-title" variant="h3" component="h2">
            {t('planLines.title')}
          </Typography>
          <Typography
            variant="caption"
            component="p"
            sx={{ color: tokens.text2, lineHeight: 1.6, mt: '2px', textWrap: 'pretty' }}
          >
            {t('planLines.blurb')}
          </Typography>
        </Box>
        {canWrite && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => openDialog({ kind: 'add' })}
            >
              {t('planLines.add')}
            </Button>
            {budget && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => openDialog({ kind: 'spread' })}
              >
                {t('planLines.spreadEvenly')}
              </Button>
            )}
          </>
        )}
      </Stack>
      {error !== null && dialog === null && (
        <Alert severity="error" sx={{ m: '12px 20px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {rows === null && <Skeleton variant="rectangular" height={120} />}
      {rows !== null && list.length === 0 && (
        <Typography
          data-plan-lines-empty=""
          sx={{ p: '22px 20px', fontSize: 13, lineHeight: 1.6, maxWidth: 560, color: tokens.text2 }}
        >
          {t('planLines.empty')}
        </Typography>
      )}
      {list.length > 0 && wide && (
        <ListTableHeader columns={COLUMNS}>
          <span />
          <span>{t('planLines.colLine')}</span>
          <span>{t('planLines.colType')}</span>
          <Box component="span" sx={{ textAlign: 'end' }}>
            {t('planLines.colAmount')}
          </Box>
          <span />
        </ListTableHeader>
      )}
      {list.map((line) => {
        const open = expanded === line.id;
        const from = line.recurrence.startOn || line.occursOn;
        return (
          <Box
            key={line.id}
            data-plan-line={line.id}
            sx={{ borderBottom: `1px solid ${tokens.line}` }}
          >
            <Box
              sx={{
                display: wide ? 'grid' : 'flex',
                gridTemplateColumns: COLUMNS,
                columnGap: 2,
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                gap: wide ? undefined : '8px',
                p: '10px 20px',
                '&:hover': { bgcolor: tokens.hover },
              }}
            >
              {toggle(line, open)}
              <Box sx={{ minWidth: 0, flex: wide ? undefined : '1 1 150px' }}>
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: wide ? 'nowrap' : 'normal',
                  }}
                >
                  {labelOf(line)}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: 11,
                    mt: '1px',
                    color: tokens.text2,
                  }}
                >
                  {t('planLines.cadenceFrom', {
                    cadence: cadenceLabel(line, locale, t),
                    date: formatCivilDate(from, locale),
                  })}
                </Typography>
              </Box>
              <Box sx={{ pt: '2px' }}>
                <Pill small tone={line.kind === 'INCOME' ? 'success' : 'warning'}>
                  {t(`planned.kind.${line.kind}`)}
                </Pill>
              </Box>
              <Box
                component="span"
                sx={{
                  textAlign: 'end',
                  fontSize: 14,
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  color: tokens.text,
                }}
              >
                <MoneyParts minor={Math.abs(line.amountMinor)} currency={currency} />
              </Box>
              {kebab(line)}
            </Box>
            {line.likelyDuplicateOf !== null && (
              // An existing line that repeats a bank series' slice (item 9): a note, nothing changed.
              <Box
                data-possible-duplicate={line.id}
                sx={{
                  pt: 0,
                  pb: '10px',
                  paddingInlineStart: wide ? '58px' : '16px',
                  paddingInlineEnd: wide ? '20px' : '16px',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                <Box component="span" sx={{ fontWeight: 500, color: tokens.warningText }}>
                  {t('planLines.possibleDuplicate')}
                </Box>
                <Box component="span" sx={{ color: tokens.text2 }}>
                  {' · '}
                  {duplicateSentence(t, line.likelyDuplicateOf, currency, locale)}
                </Box>
              </Box>
            )}
            {open && occurrences(line)}
          </Box>
        );
      })}

      {deleting.target !== null && (
        <ConfirmDialog
          title={
            deleting.target.spread
              ? t('planLines.deleteSpreadTitle', {
                  count: list.filter((row) => row.spreadGroup === deleting.target?.line.spreadGroup)
                    .length,
                })
              : t('planLines.deleteTitle', {
                  kind: t(`planned.kind.${deleting.target.line.kind}`),
                  // Unsigned, as the line itself (contract v2 §13).
                  amount: formatMoneyMinor(
                    Math.abs(deleting.target.line.amountMinor),
                    currency,
                    locale,
                  ),
                })
          }
          consequence={
            deleting.target.spread
              ? t('planLines.deleteSpreadConsequence')
              : t('planLines.deleteConsequence')
          }
          confirmLabel={
            deleting.target.spread
              ? t('planLines.deleteSpreadConfirm')
              : t('planLines.deleteConfirm')
          }
          destructive
          busy={deleting.busy}
          error={deleting.error}
          onConfirm={() => void handleDelete()}
          onCancel={deleting.cancel}
        />
      )}

      {menu !== null && (
        <ObjectMenu
          title={labelOf(menu.line)}
          anchorEl={menu.anchor}
          open
          onClose={() => setMenu(null)}
        >
          <MenuItem
            onClick={() => {
              openDialog({ kind: 'edit', line: menu.line });
              setMenu(null);
            }}
          >
            {t('planLines.edit')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              deleting.ask({ spread: false, line: menu.line });
              setMenu(null);
            }}
          >
            {t('planLines.delete')}
          </MenuItem>
          {menu.line.spreadGroup !== null && (
            <MenuItem
              onClick={() => {
                deleting.ask({ spread: true, line: menu.line });
                setMenu(null);
              }}
            >
              {t('planLines.deleteSpread')}
            </MenuItem>
          )}
        </ObjectMenu>
      )}

      {dialog?.kind === 'add' && (
        <PlanLineDialog
          title={t('planLines.addTitle')}
          envelopeName={envelopeName}
          currency={currency}
          busy={writing}
          error={error}
          errorTick={errorTick}
          nameError={nameError}
          subscribe={subscribeStep}
          duplicate={duplicate}
          onDuplicateCancel={() => setDuplicate(null)}
          onClose={() => setDialog(null)}
          onSubmit={(body) =>
            void run(async () => {
              await planLinesApi.create(envelopeId, { ...body, name: body.name ?? '' });
              setDialog(null);
            }, t('planLines.saved'))
          }
        />
      )}
      {dialog?.kind === 'edit' && (
        <PlanLineDialog
          title={t('planLines.editTitle')}
          envelopeName={envelopeName}
          currency={currency}
          busy={writing}
          error={error}
          errorTick={errorTick}
          nameError={nameError}
          subscribe={subscribeStep}
          line={dialog.line}
          duplicate={duplicate}
          onDuplicateCancel={() => setDuplicate(null)}
          onClose={() => setDialog(null)}
          onSubmit={(body) =>
            void run(async () => {
              await planLinesApi.update(envelopeId, dialog.line.id, body);
              setDialog(null);
            }, t('planLines.saved'))
          }
        />
      )}
      {dialog?.kind === 'spread' && (
        <SpreadDialog
          currency={currency}
          busy={writing}
          error={error}
          errorTick={errorTick}
          subscribe={subscribeStep}
          onClose={() => setDialog(null)}
          onSubmit={(body) =>
            void run(async () => {
              await planLinesApi.spread(envelopeId, body);
              setDialog(null);
            }, t('planLines.saved'))
          }
        />
      )}
    </Paper>
  );
}
